import { CircularProgress } from "@mui/material";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { fetchTimeOffRequestEventsByTorId } from "../api/timeOffRequest.js";
import { selectUserSession } from "../slices/userSession/userSessionSlice.js";
import {
  format_DOW_ShortMonth_Date_Year,
  format_LongMonth_Date_Year_Time,
  getShiftTime,
} from "../util/dateTimeFormatter.js";
import { getScrubbedTimeOffRequestEvents } from "../util/timeOffRequestScrubber.js";
import SlideUpTransition from "./fundamentals/SlideUpTransition";
import StarsCloseIconButton from "./fundamentals/StarsCloseIconButton.jsx";
import TimeOffRequestEmployeeAction from "./TimeOffRequestEmployeeAction";
import { isLWOPTOWOPDayOfRequest, isLWOPTOWOPRequest } from "../util/timeOffRequestUtils.js";
import { getTORApprovedLeaveTime, getTORPreferredLeaveTime } from "../util/timeOffExportMapper.js";

const TimeOffRequestDialogMobile = (props) => {
  const userSession = useSelector(selectUserSession);
  const [timeOffRequestEventList, setTimeOffRequestEventList] = useState([]);
  const [isTimeOffRequestEventListLoading, setIsTimeOffRequestEventListLoading] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const renderTORStatusHistory = () => {
    if (timeOffRequestEventList) {
      return timeOffRequestEventList.map((torEvent) => {
        if (!torEvent.flags.isAdditionalApproved) {
          return (
            <Paper className="status-history-event" key={torEvent.eventId}>
              <Grid item xs={12}>
                <div className="bold-text">
                  {torEvent.modifiedByEid === ""
                    ? torEvent.timeOffRequestStatus
                    : torEvent.timeOffRequestStatus +
                      " - " +
                      torEvent.modifiedByLastName +
                      ", " +
                      torEvent.modifiedByFirstName}
                </div>
                {torEvent.cannedReason ? <div>Reason: {torEvent.cannedReason}</div> : ""}
                {torEvent.reasonComments ? <div>Comments: {torEvent.reasonComments}</div> : ""}
                <div>{format_LongMonth_Date_Year_Time(torEvent.createdAt)}</div>
              </Grid>
            </Paper>
          );
        }
        return "";
      });
    }
  };

  useEffect(() => {
    if (isFirstLoad) {
      fetchTimeOffRequestEventsByTorId(props.timeOffRequest.torId, userSession.eid, props.isAdminPage)
        .then((res) => res.json())
        .then((result) => {
          let scrubbedTimeOffRequestEvents = getScrubbedTimeOffRequestEvents(result.timeOffRequestEvents);
          setTimeOffRequestEventList(scrubbedTimeOffRequestEvents);
          setIsTimeOffRequestEventListLoading(false);
          setIsFirstLoad(false);
        })
        .catch((err) => {
          console.error("error happened!", err);
          setIsTimeOffRequestEventListLoading(false);
          setIsFirstLoad(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog
      fullScreen
      className="detail-dialog"
      fullWidth
      maxWidth="md"
      open={props.isDialogOpen}
      TransitionComponent={SlideUpTransition}
      keepMounted
      onClose={() => props.onClickDialogClose()}
      aria-labelledby="alert-dialog-slide-title"
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogTitle id="alert-dialog-slide-title">
        <StarsCloseIconButton onClick={() => props.onClickDialogClose()} />
      </DialogTitle>
      <DialogContent className="detail-dialog-content">
        <DialogContentText component={"div"} id="alert-dialog-slide-description">
          <Grid container>
            <Grid item xs={12} sm={6}>
              <Paper className="detail-dialog-section">
                <Typography component={"div"}>
                  <Box className="detail-dialog-section-title">Time Off Information</Box>
                </Typography>
                <Grid container>
                  <Grid item xs={6}>
                    <div>Reference #</div>
                  </Grid>
                  <Grid item xs={6}>
                    <div className="detail-dialog-right-text">{props.timeOffRequest.referenceNumber}</div>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={6}>
                    <div>Type</div>
                  </Grid>
                  <Grid item xs={6}>
                    <div className="detail-dialog-right-text">{props.timeOffRequest.timeOffRequestType}</div>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={6}>
                    <div>Latest Status</div>
                  </Grid>
                  <Grid item xs={6}>
                    <div className="detail-dialog-right-text">{props.timeOffRequest.visibleStatus}</div>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={6}>
                    <div>Station</div>
                  </Grid>
                  <Grid item xs={6}>
                    <div className="detail-dialog-right-text">{props.timeOffRequest.station}</div>
                  </Grid>
                </Grid>
              </Paper>
              <Paper className="detail-dialog-section">
                <Typography component={"div"}>
                  <Box className="detail-dialog-sub-section-title">Employee</Box>
                </Typography>
                <Grid container>
                  <Grid item xs={6}>
                    <div>Name</div>
                  </Grid>
                  <Grid item xs={6}>
                    <div className="detail-dialog-right-text">
                      {props.timeOffRequest.empLastName}, {props.timeOffRequest.empFirstName}
                    </div>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={6}>
                    <div>ID</div>
                  </Grid>
                  <Grid item xs={6}>
                    <div className="detail-dialog-right-text">{props.timeOffRequest.empId}</div>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={6}>
                    <div>Workgroup</div>
                  </Grid>
                  <Grid item xs={6}>
                    <div className="detail-dialog-right-text">{props.timeOffRequest.workgroup}</div>
                  </Grid>
                  <Grid container>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    <Grid item xs={6}>
                      <div>Shift Date</div>
                    </Grid>
                    <Grid item xs={6}>
                      <div className="detail-dialog-right-text">
                        {format_DOW_ShortMonth_Date_Year(props.timeOffRequest.shiftStart)}
                      </div>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    <Grid item xs={6}>
                      <div>Shift Time</div>
                    </Grid>
                    <Grid item xs={6}>
                      <div className="detail-dialog-right-text">
                        {getShiftTime(props.timeOffRequest.shiftStart) +
                          "-" +
                          getShiftTime(props.timeOffRequest.shiftEnd)}
                      </div>
                    </Grid>
                    {isLWOPTOWOPDayOfRequest(props.timeOffRequest.timeOffRequestType) && (
                      <>
                        <Grid item xs={12}>
                          <Divider />
                        </Grid>
                        <Grid item xs={6}>
                          <div>Preferred Leave Time</div>
                        </Grid>
                        <Grid item xs={6}>
                          <div className="detail-dialog-right-text">
                            {getTORPreferredLeaveTime(props.timeOffRequest)}
                          </div>
                        </Grid>
                      </>
                    )}
                    {isLWOPTOWOPRequest(props.timeOffRequest.timeOffRequestType) &&
                      props.timeOffRequest.approvedWOPEnd &&
                      props.timeOffRequest.approvedWOPStart && (
                        <>
                          <Grid item xs={12}>
                            <Divider />
                          </Grid>
                          <Grid item xs={6}>
                            <div>Approved Leave Time</div>
                          </Grid>
                          <Grid item xs={6}>
                            <div className="detail-dialog-right-text">
                              {getTORApprovedLeaveTime(props.timeOffRequest)}
                            </div>
                          </Grid>
                        </>
                      )}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper className="detail-dialog-section">
                <Typography component={"div"}>
                  <Box className="detail-dialog-section-title">Status History</Box>
                </Typography>
                {isTimeOffRequestEventListLoading ? (
                  <CircularProgress />
                ) : (
                  <Grid container>{renderTORStatusHistory()}</Grid>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContentText>
      </DialogContent>
      <DialogActions className="mobile-action-buttons">
        <div className="dialog-right-button">
          <TimeOffRequestEmployeeAction
            timeOffRequest={props.timeOffRequest}
            onTimeOffRequestDialogClose={() => props.onClickDialogClose()}
          />
        </div>
      </DialogActions>
    </Dialog>
  );
};
export default TimeOffRequestDialogMobile;
